import React, {
	createContext,
	DetailedHTMLProps,
	FormEvent,
	FormHTMLAttributes,
	InputHTMLAttributes,
	useContext,
	useMemo,
	useRef,
	useState,
} from 'react';
import inputHasError, { SkipValidations } from './inputHasError';

export type FormValue<T = never> =
	| InputHTMLAttributes<HTMLInputElement>['value']
	| boolean
	| T;
export type FormValues<T = never> = Record<string, FormValue<T>>;
export type FormErrors = Record<string, string>;
export type Validator<
	TFieldValue = unknown,
	TFormValues extends FormValues<unknown> = FormValues,
> = (value: TFieldValue, formValues?: TFormValues) => string;
export type FormIsDirty<TValues extends FormValues<unknown> = FormValues> = {
	form: boolean;
	values: Record<keyof TValues, boolean>;
};
export type FormSubmitHandler<
	TValues extends FormValues<unknown> = FormValues,
> = (values: TValues, isDirty?: FormIsDirty<TValues>) => void;

export interface FormPropsI<
	TValues extends FormValues<unknown> = FormValues,
	TErrors extends Partial<FormErrors> = Partial<FormErrors>,
> {
	/** keys should match `name` props of input components */
	errors?: TErrors;
	/** set validity key to `true` to ignore; sometimes you want the behavior of certain input attributes but want to ignore violating the validity - for example, you may want the behavior of a granular `step` attribute but still want to allow inputs with values that violate the `step` */
	skipValidations?: SkipValidations;
	onSubmit?: FormSubmitHandler<TValues>;
	onErrorsChange?: (errors: TErrors) => void;
	onValuesChange?: (values: TValues) => void;
	validators?: {
		[K in keyof TValues]?: Validator<TValues[K], TValues>;
	};
	/** keys should match `name` props of input components */
	values: TValues;
}

export interface FormContextValueI<
	TValues extends FormValues<unknown> = FormValues,
	TErrors extends Partial<FormErrors> = Partial<FormErrors>,
> {
	errors: TErrors;
	skipValidations?: SkipValidations;
	isValid: boolean;
	onErrorsChange: (errors: TErrors) => void;
	onValuesChange: (values: TValues) => void;
	validators: {
		[K in keyof TValues]?: Validator<TValues[K], TValues>;
	};
	values: TValues;
	isDirty: FormIsDirty<TValues>;
	setIsDirty: React.Dispatch<React.SetStateAction<FormIsDirty<TValues>>>;
}

const formContext = createContext<unknown>(null!);

export const useForm = <
	TValues extends FormValues<unknown> = FormValues,
	TErrors extends Partial<FormErrors> = Partial<FormErrors>,
>(): FormContextValueI<TValues, TErrors> =>
	useContext(formContext) as FormContextValueI<TValues, TErrors>;

export const useInput = <
	TValues extends FormValues<unknown> = FormValues,
	TErrors extends Partial<FormErrors> = Partial<FormErrors>,
>(
	inputName: string,
) => {
	const {
		values,
		onValuesChange,
		errors,
		onErrorsChange,
		validators,
		skipValidations,
		setIsDirty,
	} = useForm<TValues, TErrors>();

	const onErrorChange = (error: string) => {
		onErrorsChange({ ...errors, [inputName]: error });
	};

	const onValueChange = (value: FormValue<unknown>) => {
		setIsDirty((prev) => ({
			form: true,
			values: { ...prev.values, [inputName]: true },
		}));
		onValuesChange({ ...values, [inputName]: value });
	};

	const error = errors[inputName];
	const value = values[inputName];

	const validator = validators?.[inputName];

	/**
	 * Checks for native html error and custom error. Calls error callback with
	 * error string (or empty string, if no error) as param.
	 */
	const validate = (inputElement: HTMLInputElement) => {
		const nativeError = inputHasError(inputElement, skipValidations);
		const customError = validator
			? validator(inputElement.value as TValues[typeof inputName], values)
			: '';

		const newError = nativeError || customError;
		onErrorChange(newError);
		return newError;
	};

	/**
	 * Just checks for isRequired and any custom validation
	 */
	const validateSimple = (
		selectValue: FormValue<unknown>,
		isRequired?: boolean,
	) => {
		const requiredError =
			isRequired &&
			(selectValue === '' || selectValue === null || selectValue === undefined)
				? 'Selecting an option is required.'
				: '';
		const customError = validator
			? validator(selectValue as TValues[typeof inputName], values)
			: '';

		const newError = requiredError || customError;
		onErrorChange(newError);
	};

	return {
		error,
		onValueChange,
		validate,
		validateSimple,
		value,
	};
};

/**
 * A form context that allows for consistent valiating while interacting with
 * the form. Validates prior to submit with native browser validation (see
 * below). Additionally, allows for custom validation. The same validation can
 * be accessed and used in child components' events with the `useInput` hook.
 * Form validity can be accesed with the `useForm` hook.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLObjectElement/validationMessage
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLObjectElement/validity
 * @example
 * const MyForm = ({ onSubmit }) => {
 *  	const [formValues, setFormValues] = useState({ firstName: '' });
 *  	const [formErrors, setFormErrors] = useState({ firstName: '' });
 *
 *  	return (
 *  		<LWForm
 *  			values={formValues}
 *  			onValuesChange={setFormValues}
 *  			errors={formErrors}
 *  			onErrorsChange={setFormErrors}
 *  			onSubmit={(values) => {
 *  				onSubmit(values)
 *  			}}
 *  		>
 *  			<HookedTextField name="firstName" label="First Name" required />
 *  			<HookedSubmit>Submit</HookedSubmit>
 *  		</LWForm>
 *  	);
 *  };
 * }
 */
const LWForm = <
	TValues extends FormValues<unknown> = FormValues,
	TErrors extends Partial<FormErrors> = Partial<FormErrors>,
>({
	children,
	errors = {} as TErrors,
	skipValidations,
	noValidate = true,
	onErrorsChange = () => {},
	onSubmit,
	onValuesChange,
	validators = {},
	values,
	...rest
}: Omit<
	DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>,
	'onSubmit'
> &
	FormPropsI<TValues, TErrors>) => {
	const initDirtyValues = Object.fromEntries(
		Object.keys(values || {}).map((key) => [key, false]),
	) as FormIsDirty<TValues>['values'];
	const [isDirty, setIsDirty] = useState<FormIsDirty<TValues>>({
		form: false,
		values: initDirtyValues,
	});

	const formRef = useRef<HTMLFormElement | null>(null);

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsDirty({ form: false, values: initDirtyValues });

		const newErrors: FormErrors = {};

		// validate native errors
		event.target
			// @ts-expect-error
			.querySelectorAll('input, textarea')
			.forEach((inputEl: HTMLInputElement | HTMLTextAreaElement) => {
				// skip inputs that are validated as a group
				if (
					inputEl.type === 'radio' ||
					inputEl.classList.contains('input-HookedTextFieldChips') ||
					inputEl.classList.contains('input-HookedTextFieldList')
				) {
					return;
				}

				const nativeError = inputHasError(inputEl, skipValidations);

				if (!nativeError) {
					return;
				}

				newErrors[inputEl.name] = nativeError;
			});

		// validate custom errors
		Object.entries(validators).forEach(([inputName, validator]) => {
			const error = validator(values[inputName], values);
			if (error) {
				newErrors[inputName] = error;
			}
		});

		// focus the first input that has an error
		if (Object.keys(newErrors).length > 0) {
			const firstErrorInput = formRef.current?.querySelector<
				HTMLInputElement | HTMLTextAreaElement
			>(`[name="${Object.keys(newErrors)[0]}"]`);
			firstErrorInput?.focus();
		}

		onErrorsChange(newErrors as TErrors);

		if (Object.values(newErrors).some((err) => Boolean(err))) {
			return;
		}

		onSubmit?.(values, isDirty);
	};

	const isValid = useMemo(
		() => Object.values(errors).every((err) => !err),
		[errors],
	);

	return (
		<formContext.Provider
			value={{
				errors,
				skipValidations,
				isValid,
				onErrorsChange,
				onValuesChange: onValuesChange || (() => {}),
				validators,
				values,
				isDirty,
				setIsDirty,
			}}
		>
			<form
				ref={formRef}
				onSubmit={handleSubmit}
				noValidate={noValidate}
				{...rest}
			>
				{children}
			</form>
		</formContext.Provider>
	);
};

export default LWForm;
